import { uiText } from '../i18n/uiText';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, CheckCircle2, Clock, Mail, MapPin, UserPlus, Users } from 'lucide-react';
import { companiesApi, getApiErrorMessage } from '../api';
import ProvisionedAccessCard from '../components/common/ProvisionedAccessCard';
import PageIntro from '../components/ui/PageIntro';
import RussianPhoneInput from '../components/common/RussianPhoneInput';
import { useAuth } from '../store/AuthContext';
import useRecordFormDraft from '../hooks/useRecordFormDraft';
import FormDraftNotice from '../components/common/FormDraftNotice';

const emptyInvite = {
  email: '',
  full_name: '',
  representative_title: '',
  phone: '',
};

export default function MyOrganizationPage() {
  const [company, setCompany] = useState(null);
  const { user } = useAuth();
  const editor = useRecordFormDraft(user?.id, company?.id || 'loading', 'my-organization-invite', emptyInvite);
  const { form, buffer } = editor;
  const setForm = value => { if (!saving && !buffer.recovery) editor.setForm(value); };
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await companiesApi.my();
      setCompany(response.data);
      setError('');
    } catch (err) {
      setError(getApiErrorMessage(err) || uiText("Не удалось загрузить организацию"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (saving || loading || buffer.recovery) return;
    const snapshot = buffer.snapshot();
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await companiesApi.inviteMyMember(form);
      setAccess({ ...response.data?.credentials, ...response.data?.delivery });
      if (editor.saved(snapshot)) setInviteOpen(false);
      setNotice(
        response.data?.delivery?.status === 'smtp_not_configured'
          ? uiText("Сотрудник добавлен. Почта пока не настроена — сохраните данные доступа ниже.")
          : uiText("Сотрудник добавлен. Письмо с доступом поставлено в очередь отправки.")
      );
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err) || uiText("Не удалось пригласить сотрудника"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="workspace-page my-organization-page auditee-portal-page">
      <PageIntro
        icon={Building2}
        title={uiText("Моя организация")}
        eyebrow={uiText("Личный кабинет")}
        description={uiText("Данные компании и сотрудники, которым предоставлен доступ к Audit Pro.")}
        aside={(
          <button type="button" className="btn btn-primary" onClick={() => setInviteOpen((value) => !value)}>
            <UserPlus size={15} /> {inviteOpen ? uiText("Закрыть форму") : uiText("Пригласить сотрудника")}
          </button>
        )}
      />

      {error && <div className="alert alert-error">{error}</div>}
      <FormDraftNotice buffer={buffer} label={uiText("Приглашение в организацию")} hidden={!inviteOpen} onResume={() => setInviteOpen(true)} disabled={saving} />
      {notice && <div className="alert alert-success"><CheckCircle2 size={16} /> {notice}</div>}
      <ProvisionedAccessCard access={access} onClose={() => setAccess(null)} />

      {loading ? <div className="loading-center"><div className="spinner" /></div> : company && (
        <>
          {process.env.REACT_APP_ENABLE_QUALIFICATION === 'true' && <Link className="ap-button ap-button--primary" to={`/suppliers/${company.id}`}>{uiText("Профиль: заявленные работы, требования и результаты")}</Link>}
          <div className="my-organization-summary">
            <div><Building2 size={18} /><span><small>{uiText("Организация")}</small><strong>{company.name}</strong></span></div>
            <div><span><small>{uiText("ИНН")}</small><strong>{company.inn || '—'}</strong></span></div>
            <div><Mail size={18} /><span><small>{uiText("Контакты представителей")}</small><strong>{(company.representative_contacts || []).map(contact => contact.email).filter(Boolean).join(', ') || uiText("Без представителя")}</strong></span></div>
            <div><MapPin size={18} /><span><small>{uiText("Адрес")}</small><strong>{company.site_address || company.legal_address || '—'}</strong></span></div>
          </div>

          <div className="my-organization-grid">
            <section className="card my-organization-team-card">
              <div className="card-header">
                <div>
                  <div className="card-title">{uiText("Команда организации")}</div>
                  <div className="workspace-muted">{company.member_count || 0}{uiText(" сотрудников с доступом к кабинету")}</div>
                </div>
              </div>
              <div className="card-body">
                <div className="my-organization-member-list">
                  {(company.members || []).map((member) => {
                    const ready = member.is_active && !member.must_change_password;
                    return (
                      <div className="my-organization-member" key={member.id}>
                        <div className="my-organization-member-avatar" aria-hidden="true">
                          {(member.full_name || 'С').split(' ').map((part) => part[0]).slice(0, 2).join('').toUpperCase()}
                        </div>
                        <div className="my-organization-member-copy">
                          <strong>{member.full_name}</strong>
                          <span>{member.representative_title || uiText("Представитель компании")}</span>
                          <span>{[member.email, member.phone].filter(Boolean).join(' · ')}</span>
                        </div>
                        <span className={`ui-badge ${ready ? 'ui-badge-success' : 'ui-badge-warning'}`}>
                          {ready ? <CheckCircle2 size={12} /> : <Clock size={12} />}
                          {ready ? uiText("Активен") : uiText("Ожидает первого входа")}
                        </span>
                      </div>
                    );
                  })}
                  {!(company.members || []).length ? (
                    <div className="auditee-empty-panel">
                      <span className="auditee-empty-icon"><Users size={20} /></span>
                      <div><strong>{uiText("В организации пока нет сотрудников")}</strong><p>{uiText("Пригласите первого коллегу — система отправит ему временный доступ.")}</p></div>
                    </div>
                  ) : null}
                </div>
              </div>
            </section>

            {inviteOpen ? <section className="card my-organization-invite-card">
              <div className="card-header"><div><div className="card-title">{uiText("Пригласить сотрудника")}</div><div className="workspace-muted">{uiText("Доступ будет только к вашей организации")}</div></div></div>
              <div className="card-body">
                <div className="my-organization-invite-note"><UserPlus size={18} /><span>{uiText("Система автоматически создаст логин и временный пароль. После первого входа сотрудник установит личный пароль.")}</span></div>
                <form onSubmit={submit} className="my-organization-invite-form">
                  <div className="form-group"><label className="form-label required">Email</label><input className="form-control" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="employee@example.ru" /></div>
                  <div className="form-group"><label className="form-label required">{uiText("ФИО")}</label><input className="form-control" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
                  <div className="form-group"><label className="form-label required">{uiText("Должность")}</label><input className="form-control" required value={form.representative_title} onChange={(e) => setForm({ ...form, representative_title: e.target.value })} /></div>
                  <div className="form-group"><label className="form-label">{uiText("Телефон")}</label><RussianPhoneInput value={form.phone} onValueChange={(phone) => setForm({ ...form, phone })} /></div>
                  <button type="submit" className="btn btn-primary" disabled={saving}><Users size={15} /> {saving ? uiText("Создаём доступ…") : uiText("Создать и отправить доступ")}</button>
                </form>
              </div>
            </section> : null}
          </div>
        </>
      )}
    </div>
  );
}
